import type {
  AuthInvitedUser,
  AuthUser,
} from '@robovision/rvai-auth'
import { naturalSort } from '@robovision/rvai-utils'
import { MemberRole } from './MemberRole'


/**
 * Account Status
 * Kept temporarily as an enum here awaiting members restructuring
 */
export enum AuthAccountStatus {
  Active = 'Active',
  InviteExpired = 'Invite Expired',
  InvitePending = 'Invite Pending',
  NotAcceptedTOS = 'Not Accepted TOS',
  ResetRequested = 'Reset Requested',
}

function parseAccountStatus (user: AuthUser): AuthAccountStatus {
  if (!user.enabled) {
    if (user.invitationTokenExpirationTimestamp) {
      const exp = new Date(user.invitationTokenExpirationTimestamp)
      if (exp > new Date()) {
        return AuthAccountStatus.InvitePending
      }
    }
    // Either expired, or no expiration timestamp (should not happen)
    return AuthAccountStatus.InviteExpired
  } else {
    switch (true) {
      case user.resetRequested: return AuthAccountStatus.ResetRequested
      case !user.tosAcceptanceTimestamp: return AuthAccountStatus.NotAcceptedTOS
      default: return AuthAccountStatus.Active
    }
  }
}

interface MemberParams {
  createdAt: Date
  email: string
  enabled: boolean
  firstName: string
  id: string
  invitationToken?: string
  invitationTokenExpirationTimestamp?: Date
  lastLogin?: Date
  lastName: string
  resetRequested: boolean
  role: MemberRole
  status: AuthAccountStatus
  tosAcceptanceTimestamp?: Date
}

const StatusOrder: Record<AuthAccountStatus, number> = [
  AuthAccountStatus.InviteExpired,
  AuthAccountStatus.InvitePending,
  AuthAccountStatus.NotAcceptedTOS,
  AuthAccountStatus.ResetRequested,
  AuthAccountStatus.Active,
].reduce((acc, val, index) => ({
  ...acc,
  [val]: index,
}), {} as Record<AuthAccountStatus, number>)

export class Member {
  static readonly modelName = 'Member'

  public immutable: boolean = false

  public readonly createdAt: Date
  public readonly email: string
  public readonly enabled: boolean
  public readonly firstName: string
  public readonly id: string
  public readonly invitationTokenExpirationTimestamp?: Date
  public readonly lastName: string
  public readonly lastLogin?: Date
  public readonly resetRequested: boolean
  public readonly role: MemberRole
  public readonly tosAcceptanceTimestamp?: Date
  public readonly status: AuthAccountStatus
  public invitationToken: string | undefined

  public constructor (params: MemberParams) {
    this.createdAt = params.createdAt
    this.email = params.email
    this.enabled = params.enabled
    this.id = params.id
    this.invitationToken = params.invitationToken
    this.invitationTokenExpirationTimestamp = params.invitationTokenExpirationTimestamp
    this.lastLogin = params.lastLogin
    this.firstName = params.firstName
    this.lastName = params.lastName
    this.resetRequested = params.resetRequested
    this.role = params.role
    this.tosAcceptanceTimestamp = params.tosAcceptanceTimestamp
    this.status = params.status
  }

  public get invitationLink (): string {
    return this.invitationToken ? `${window.location.origin}/signup?invitationToken=${this.invitationToken}` : ''
  }

  public get resetPasswordLink (): string {
    return this.invitationToken ? `${window.location.origin}/signup?resetToken=${this.invitationToken}` : ''
  }

  public static fromResponse (data: AuthUser | AuthInvitedUser): Member {
    const tokenExp = data.invitationTokenExpirationTimestamp
    return new Member({
      createdAt: new Date(data.createdAt),
      email: data.email,
      enabled: data.enabled,
      firstName: data.firstName,
      id: data.id,
      invitationToken: 'invitationToken' in data ? data.invitationToken : undefined,
      invitationTokenExpirationTimestamp: tokenExp ? new Date(tokenExp) : undefined,
      lastLogin: data.lastLogin ? new Date(data.lastLogin) : undefined,
      lastName: data.lastName,
      resetRequested: data.resetRequested,
      role: MemberRole.fromResponse(data.role),
      status: parseAccountStatus(data),
      tosAcceptanceTimestamp: data.tosAcceptanceTimestamp ? new Date(data.tosAcceptanceTimestamp) : undefined,
    })
  }

  public static sort (a: Member, b: Member): number {
    // first sort by status
    if (a.status !== b.status) {
      return StatusOrder[a.status] < StatusOrder[b.status] ? -1 : 1
    }

    // then by email
    return naturalSort(a.email, b.email)
  }
}
